<template>
  <div class="release-note-entry">
    <h3 class="_title">
      {{ releaseNote.title }}
    </h3>
    <div
      v-if="releaseNote.tags.length > 0"
      class="_tags"
    >
      <m-tag
        v-for="tag in releaseNote.tags"
        :key="tag"
        class="_tag"
        :title="tag"
        color="light"
      />
    </div>
    <div class="_content">
      <portable-text
        :value="releaseNote.content"
        :components="portableTextComponents"
      />
    </div>
    <release-sections
      v-if="sections.length > 0"
      class="_sections"
      :sections="sections"
    />
  </div>
</template>

<script setup>
import CodeBlockComponent from '@/release-notes/CodeBlockComponent.vue';
import ImageComponent from '@/release-notes/ImageComponent.vue';
import ReleaseSections from '@/release-notes/ReleaseSections.vue';
import VideoComponent from '@/release-notes/VideoComponent.vue';
import { PortableText } from '@portabletext/vue';
import { computed } from 'vue';
import { portableTextMarks } from '@/release-notes/portable-text-marks';

const props = defineProps({
  releaseNote: {
    type: Object,
    required: true,
  },
});

const sections = computed(() => props.releaseNote.sections ?? []);

const portableTextComponents = {
  block: { code: CodeBlockComponent },
  types: {
    imageEmbed: ImageComponent,
    videoEmbed: VideoComponent,
  },
  marks: portableTextMarks,
};
</script>

<style scoped lang="scss" type="text/scss">
  .release-note-entry {
    ._title {
      margin: 0;
      font-size: $font-size-8;
      color: $font-color-primary;
    }

    ._tags {
      display: flex;
      flex-flow: wrap;
      gap: 1rem;
      margin-top: 1rem;
    }

    ._content {
      margin-top: 1.6rem;
      font-size: $font-size-4;
      line-height: 1.6;
      color: $font-color-primary;
    }

    ._sections {
      margin-top: 3.2rem;
    }
  }
</style>

<style lang="scss" type="text/scss">
  // Unscoped on purpose: portable text renders its blocks as a fragment, so
  // they never carry this component's scope id and scoped rules can't match.
  .release-note-entry ._content {
    > :first-child {
      margin-top: 0;
    }

    p {
      margin: 0 0 1.2rem;
    }

    h2,
    h3,
    h4 {
      font-weight: $font-weight-semibold;
      color: $font-color-primary;
    }

    h2 {
      margin: 3.2rem 0 .8rem;
      font-size: $font-size-7;
      line-height: 1.35;
    }

    h3 {
      margin: 2.8rem 0 .8rem;
      font-size: $font-size-6;
      line-height: 1.4;
    }

    h4 {
      margin: 2.4rem 0 .6rem;
      font-size: $font-size-5;
      line-height: 1.45;
    }

    ul,
    ol {
      padding-inline-start: 2.4rem;
      margin: 0 0 1.2rem;
    }

    li + li {
      margin-top: .6rem;
    }
  }
</style>
